import { registerPatient } from "../slices/patientSlice";
import { logErrorEvent } from "../services/logService";

/**
 * Fetch a Patient by ID
 * - Checks Redux store first
 * - Fetches from FHIR server if missing
 * - Registers into Redux
 */
export const fetchPatient = async (patientId, fhirClient, store, { forceFetch = false } = {}) => {
    if (!patientId || !fhirClient) return null;

    // Check Redux store
    const cached = store.getState().patients?.[patientId];
    if (cached && !forceFetch) {
        return cached;
    }

    try {
        // console.log(`🌐 Fetching Patient/${patientId} from FHIR`);
        const fetched = await fhirClient.request(`Patient/${patientId}`);
        if (!fetched?.resourceType === "Patient") {
            console.warn("⚠️ Unexpected patient response:", fetched);
            return null;
        }

        store.dispatch(registerPatient(fetched));
        return fetched;

    } catch (err) {
        console.error("❌ Failed to fetch patient:", err);
        const status = err?.response?.status || err?.status || 500;
        logErrorEvent({
            errorType: "read",
            error: {
                message: err?.message || "Patient fetch failed",
                status,
                resourceId: `Patient/${patientId}`
            },
            description: `Failed to retrieve Patient/${patientId} from FHIR server`,
            store
        });
        return { error: status };
    }
};

/**
 * Fetch a patient given a FHIR reference string
 * e.g. "Patient/123" or "Patient/123/_history/4"
 */
export const fetchPatientByReference = async (ref, fhirClient, store, options = {}) => {
    if (!ref || typeof ref !== "string") {
        console.warn("⚠️ Invalid patient reference (not a string):", ref);
        return null;
    }

    if (!ref.startsWith("Patient/")) {
        console.warn("⚠️ Reference is not a Patient:", ref);
        return null;
    }

    // Extract ID safely even if versioned
    // "Patient/123" → "123"
    // "Patient/123/_history/5" → "123"
    const parts = ref.split("/");
    const patientId = parts[1];

    if (!patientId || patientId === "undefined" || patientId === "null") {
        console.warn("⚠️ Could not extract valid patient ID from reference:", ref);
        return null;
    }

    return fetchPatient(patientId, fhirClient, store, options);
};
